import { type SelectionsReader } from '@databases/selections-api-types';
import { getService } from '@databases/service-container';
import { useLayoutEffect, useRef, useState } from 'react';

import { databasesPingRegistry } from './ping-registry';

/**
 * Bridges **non-React** database selection state into React for a single component instance.
 *
 * Selection updates are published via the typed ping registry (`database` → `selectionChanged`).
 * This hook subscribes once per mount, re-reads {@link SelectionsReader} whenever that ping fires,
 * and stores the result in React state. The `read` callback may return **any** shape (primitives,
 * objects, arrays); unlike `useSyncExternalStore`, there is no snapshot referential-identity
 * contract on the return value.
 *
 * **`read` and closures:** `read` is kept in a ref, so you may pass an inline function. Values
 * from outer scope (e.g. `entryId`) are always the latest when a ping runs. Initial state uses
 * the `read` from the first render.
 *
 * **Lifecycle:** Subscribes in `useLayoutEffect` with an empty dependency list. If the database /
 * selection context changes, assume the component **unmounts** (e.g. after a selections rebuild);
 * do not rely on this hook to track `contentId` changes inside one long-lived instance unless
 * you control remounting (e.g. `key` on a parent).
 *
 * ---
 *
 * ### Use **very sparingly**
 *
 * Canonical selection is intentionally **outside** the main React tree for performance on large,
 * grid-heavy surfaces. Every subscription can contribute to work on selection changes. **Do not**
 * use this for the grid body, per-cell chrome at scale, or any high-frequency path. Prefer the
 * existing non-React selection pipeline there.
 *
 * Reserve this hook for **low fan-out** UI: devtools, isolated panels, or a small number of
 * components that truly must re-render from aggregate selection. When `databasesSelectionsPerformance`
 * is off, many flows should keep using Redux selectors instead.
 *
 * For non-React code, subscribe with `key('database', {})` and `subscribeTo(..., 'selectionChanged', ...)`
 * from `@databases/ping-registry`, then read `getService('SelectionsReader')`.
 *
 * @param read - Pure projection from `SelectionsReader` to the value this component needs.
 *
 * @returns The latest `read` result; updates after each `selectionChanged` ping (and once on mount).
 *
 * @example
 * ```tsx
 * const isOnlyThisValueSelected = useDatabaseSelectionSnapshot((reader) =>
 *   reader.getSelectionCount() === 1 && reader.isValueSelected(entryId, fieldId)
 * );
 * ```
 *
 * @example
 * ```tsx
 * const selection = useDatabaseSelectionSnapshot((reader) => ({
 *   fields: reader.getSelectedFieldIds(),
 *   entries: reader.getSelectedEntryIds(),
 *   values: reader.getSelectedValues(),
 * }));
 * ```
 */
export function useDatabaseSelectionSnapshot<T>(
  read: (reader: SelectionsReader) => T
): T {
  const readRef = useRef(read);

  readRef.current = read;

  const [state, setState] = useState(() =>
    readRef.current(getService('SelectionsReader'))
  );

  useLayoutEffect(() => {
    const sync = () => {
      setState(readRef.current(getService('SelectionsReader')));
    };

    const pingKey = databasesPingRegistry.key('database', {});
    const unsubscribe = databasesPingRegistry.subscribeTo(
      pingKey,
      'selectionChanged',
      sync
    );

    sync();

    return unsubscribe;
  }, []);

  return state;
}
